import { useState, useEffect, useRef } from "react";
import { Cake, Plus, Loader2, Upload, Trash2, ChevronRight, ChevronLeft, Users, MessageSquare, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import API from "@/lib/api";
import { useNavigate } from "react-router-dom";

function getSmsInfo(message) {
  const GSM7 = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà\^{}\\\[~\]|€]*$/;
  const isGsm = GSM7.test(message);
  const len = isGsm ? [...message].reduce((a, c) => a + ("^{}\\[~]|€".includes(c) ? 2 : 1), 0) : message.length;
  const sl = isGsm ? 160 : 70;
  const ml = isGsm ? 153 : 67;
  const segs = len === 0 ? 0 : len <= sl ? 1 : Math.ceil(len / ml);
  return { encoding: isGsm ? "GSM-7" : "Unicode", charCount: len, segments: segs };
}

export default function Birthday() {
  const navigate = useNavigate();
  const [hasSubscription, setHasSubscription] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [senderIds, setSenderIds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creditsPerSegment, setCreditsPerSegment] = useState(1);
  const [form, setForm] = useState({ name: "", message_template: "Happy Birthday {name}! Wishing you a wonderful day.", sender_id: "" });
  const [saving, setSaving] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "", birthday_date: "" });
  const [addingContact, setAddingContact] = useState(false);
  const [uploadingContacts, setUploadingContacts] = useState(false);
  const fileRef = useRef();

  useEffect(() => { checkSubscription(); }, []);

  const checkSubscription = async () => {
    try {
      const [subRes, sidRes, costRes] = await Promise.all([
        API.get("/subscriptions/current"),
        API.get("/sender-ids"),
        API.get("/sms/cost-info"),
      ]);
      const sub = subRes.data?.subscription;
      const activeSub = sub && (sub.status === "active" || sub.status === "trial") ? sub : null;
      setHasSubscription(!!activeSub);
      setCreditsPerSegment(costRes.data?.credits_per_segment || 1);
      if (activeSub) {
        setSenderIds((sidRes.data?.sender_ids || []).filter(s => s.status === "approved"));
        loadCampaigns();
      } else {
        setLoading(false);
      }
    } catch {
      setHasSubscription(false);
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await API.get("/birthday/campaigns");
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      if (e.response?.data?.detail === "SUBSCRIPTION_REQUIRED") setHasSubscription(false);
      else toast.error("Failed to load campaigns");
    } finally { setLoading(false); }
  };

  const loadContacts = async (cid) => {
    try {
      const res = await API.get(`/birthday/campaigns/${cid}/contacts`);
      setContacts(res.data.contacts || []);
    } catch { toast.error("Failed to load contacts"); }
  };

  const createCampaign = async () => {
    if (!form.name || !form.message_template || !form.sender_id) {
      toast.error("Name, message and sender ID are required");
      return;
    }
    setSaving(true);
    try {
      await API.post("/birthday/campaigns", form);
      toast.success("Birthday campaign created");
      setShowCreate(false);
      setForm({ name: "", message_template: "Happy Birthday {name}! Wishing you a wonderful day.", sender_id: "" });
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create campaign"); }
    finally { setSaving(false); }
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm("Delete this birthday campaign and all its contacts?")) return;
    try {
      await API.delete(`/birthday/campaigns/${cid}`);
      toast.success("Campaign deleted");
      if (selected?._id === cid) setSelected(null);
      loadCampaigns();
    } catch { toast.error("Failed to delete"); }
  };

  const openCampaign = (c) => {
    setSelected(c);
    loadContacts(c._id);
  };

  const addContact = async () => {
    if (!contactForm.phone || !contactForm.birthday_date) {
      toast.error("Phone and birthday date are required (MM-DD)");
      return;
    }
    setAddingContact(true);
    try {
      await API.post(`/birthday/campaigns/${selected._id}/contacts`, contactForm);
      toast.success("Contact added");
      setContactForm({ name: "", phone: "", birthday_date: "" });
      loadContacts(selected._id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add contact"); }
    finally { setAddingContact(false); }
  };

  const uploadContacts = async () => {
    if (!fileRef.current?.files?.[0]) { toast.error("Select an Excel file"); return; }
    setUploadingContacts(true);
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    try {
      const res = await API.post(`/birthday/campaigns/${selected._id}/contacts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data.message);
      if (fileRef.current) fileRef.current.value = "";
      loadContacts(selected._id);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingContacts(false); }
  };

  const deleteContact = async (cid, contactId) => {
    try {
      await API.delete(`/birthday/campaigns/${cid}/contacts/${contactId}`);
      setContacts(c => c.filter(x => x._id !== contactId));
      toast.success("Contact removed");
    } catch { toast.error("Failed to remove"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={24} className="animate-spin text-[#0c1e41]" />
    </div>
  );

  if (hasSubscription === false) return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm max-w-md mx-auto mt-10">
      <div className="w-14 h-14 rounded-full bg-[#eef1f8] border border-[#c0cfe8] flex items-center justify-center mx-auto mb-4">
        <Lock size={24} className="text-[#0c1e41]" />
      </div>
      <h2 className="text-lg font-bold text-slate-900">Subscription Required</h2>
      <p className="text-slate-500 text-sm mt-2 leading-relaxed">Birthday SMS automation is available to subscribed customers. Subscribe to a plan to unlock this feature.</p>
      <Button className="mt-6 bg-[#0c1e41] hover:bg-[#091629] w-full" onClick={() => navigate("/dashboard/subscribe")} data-testid="birthday-subscribe-btn">
        View Subscription Plans
      </Button>
    </div>
  );

  // Campaign detail view
  if (selected) return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => setSelected(null)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="back-to-campaigns">
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-extrabold text-slate-900">{selected.name}</h1>
          <p className="text-xs text-slate-500 mt-0.5">Sender: <span className="font-semibold text-[#0c1e41]">{selected.sender_id}</span></p>
        </div>
      </div>

      {/* Message template */}
      <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4">
        <p className="text-xs font-bold text-[#0c1e41] mb-1">Birthday Message</p>
        <p className="text-sm text-slate-700">{selected.message_template}</p>
        <p className="text-xs text-slate-500 mt-1">Sends daily at 9 AM WAT to contacts whose birthday is today.</p>
      </div>

      {/* Add contact form */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Add Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Name</Label>
            <Input value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name" className="h-8 text-xs mt-1" data-testid="birthday-contact-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Phone *</Label>
            <Input value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="08012345678" className="h-8 text-xs mt-1" data-testid="birthday-contact-phone" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Birthday (MM-DD) *</Label>
            <Input value={contactForm.birthday_date} onChange={e => setContactForm({ ...contactForm, birthday_date: e.target.value })} placeholder="12-25" className="h-8 text-xs mt-1" data-testid="birthday-contact-date" />
          </div>
        </div>
        <Button onClick={addContact} disabled={addingContact} size="sm" className="mt-3 bg-[#0c1e41] hover:bg-[#091629] text-xs h-8" data-testid="add-birthday-contact-btn">
          {addingContact ? <Loader2 size={12} className="animate-spin mr-1" /> : <Plus size={12} className="mr-1" />} Add Contact
        </Button>
      </div>

      {/* Bulk upload */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-2">Bulk Upload (Excel)</h3>
        <p className="text-xs text-slate-500 mb-3">Required columns: <span className="font-mono font-semibold">phone</span>, <span className="font-mono font-semibold">birthday</span> (MM-DD). Optional: name, notes.</p>
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#eef1f8] file:text-[#0c1e41] file:text-xs file:font-medium cursor-pointer" data-testid="birthday-upload-input" />
          <Button size="sm" onClick={uploadContacts} disabled={uploadingContacts} className="bg-[#0c1e41] hover:bg-[#091629] h-8 text-xs" data-testid="birthday-upload-btn">
            {uploadingContacts ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Upload
          </Button>
        </div>
      </div>

      {/* Contacts list */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Contacts ({contacts.length})</h3>
        </div>
        {contacts.length === 0 ? (
          <div className="p-8 text-center">
            <Users size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">No contacts yet. Add or upload contacts above.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {contacts.map(c => (
              <div key={c._id} className="flex items-center justify-between px-4 py-3" data-testid={`birthday-contact-row-${c._id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{c.name || c.phone}</p>
                  <p className="text-xs text-slate-500">{c.phone} · Birthday: {c.birthday_date}</p>
                </div>
                <button onClick={() => deleteContact(selected._id, c._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-birthday-contact-${c._id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Campaigns list
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Birthday SMS</h1>
          <p className="text-sm text-slate-500 mt-1">Automatically send birthday messages to your clients at 9 AM WAT</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="create-birthday-campaign-btn">
          <Plus size={15} className="mr-2" /> New Campaign
        </Button>
      </div>

      {/* Create campaign form */}
      {showCreate && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">New Birthday Campaign</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-700">Campaign Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Customer Birthdays 2026" className="h-9 text-sm" data-testid="birthday-campaign-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-700">Sender ID *</Label>
                <select
                  value={form.sender_id}
                  onChange={e => setForm({ ...form, sender_id: e.target.value })}
                  className="w-full h-9 border border-slate-300 rounded-md px-3 text-sm text-slate-900 bg-white focus:outline-none focus:border-[#0c1e41]"
                  data-testid="birthday-sender-id"
                >
                  <option value="">Select Sender ID</option>
                  {senderIds.map(s => (
                    <option key={s._id} value={s.sender_id}>{s.sender_id}</option>
                  ))}
                </select>
                {senderIds.length === 0 && (
                  <p className="text-xs text-amber-600">No approved Sender IDs. Request one in Sender IDs tab.</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Message Template * <span className="font-normal text-slate-400 ml-1">Use {"{name}"} for client name</span></Label>
              <textarea
                value={form.message_template}
                onChange={e => setForm({ ...form, message_template: e.target.value })}
                rows={3}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 bg-white resize-none focus:outline-none focus:border-[#0c1e41] placeholder:text-slate-400"
                placeholder="Happy Birthday {name}! Wishing you a wonderful day."
                data-testid="birthday-message-template"
              />
              <div className="text-xs text-slate-400 flex items-center justify-between">
                <span>{form.message_template.length} characters</span>
                <span>
                  {getSmsInfo(form.message_template).segments || 1} segment(s) ·{" "}
                  <span className="text-[#0c1e41] font-medium">
                    {(getSmsInfo(form.message_template).segments || 1) * creditsPerSegment} credit{(getSmsInfo(form.message_template).segments || 1) * creditsPerSegment !== 1 ? "s" : ""}/message
                  </span>
                </span>
              </div>
              {/* Live Preview */}
              {form.message_template.trim() && (
                <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-3 mt-1">
                  <p className="text-xs font-bold text-[#0c1e41] mb-1.5">Preview (sample name: "James")</p>
                  <div className="bg-white border border-[#c0cfe8]/60 rounded-lg p-3">
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                      {form.message_template.replace(/\{name\}/g, "James")}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs text-slate-400">From: <span className="font-semibold text-[#0c1e41]">{form.sender_id || "SENDER"}</span></p>
                    <div className="flex items-center gap-1 text-xs text-[#0c1e41] font-medium">
                      <Zap size={11} />
                      <span>{(getSmsInfo(form.message_template).segments || 1) * creditsPerSegment} credit{(getSmsInfo(form.message_template).segments || 1) * creditsPerSegment !== 1 ? "s" : ""} per birthday message</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button onClick={createCampaign} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] text-sm shadow-sm" data-testid="save-birthday-campaign-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Create Campaign
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)} className="text-sm">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns list */}
      {campaigns.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <Cake size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No birthday campaigns yet</p>
          <p className="text-slate-400 text-sm mt-1">Create a campaign and add your clients' birthdays</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between gap-4" data-testid={`birthday-campaign-row-${c._id}`} onClick={() => openCampaign(c)}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#eef1f8] border border-[#c0cfe8] flex items-center justify-center shrink-0">
                  <Cake size={18} className="text-[#0c1e41]" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    Sender: {c.sender_id} · {c.contact_count || 0} contacts
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${c.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {c.status}
                </span>
                <button
                  onClick={e => { e.stopPropagation(); deleteCampaign(c._id); }}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid={`delete-campaign-btn-${c._id}`}
                >
                  <Trash2 size={13} />
                </button>
                <ChevronRight size={16} className="text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
